/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Button,
  Fieldset,
  FormContainer,
  FormItem,
  Input,
  Modal,
  NativeSelect,
} from 'elmo-elements';
import { ElementType } from '@/modules/types/element';
import { useCreateElementMutation } from '@/modules/services';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import CKEditorWrapper from '@/components/CKEditorWrapper';
import { getCkEditorConfig } from '@/lib/util';

interface TooltipModalProps {
  isOpen: boolean;
  tooltipData: ElementType | null;
  onClose: () => void;
  onSave: () => void;
  frameId?: string;
  totalTooltip: number;
  elearningId?: string;
  tenantId?: string;
}

interface ElementFormType {
  id: string;
  name: string;
  content: string;
  elementMetadata: {
    elementType: number;
    width: number;
    height: number;
    position: number;
  };
  frameId?: string;
}

type FieldNameType = 'name' | 'content' | 'elementMetadata.elementType';

const TooltipModal = ({
  isOpen,
  tooltipData,
  onClose,
  frameId,
  onSave,
  totalTooltip,
  elearningId,
  tenantId,
}: TooltipModalProps) => {
  const [postElement, postElementStatus] = useCreateElementMutation();
  const dispatch = useDispatch();

  const noteTypes = [
    { label: 'Inline', value: '1' },
    { label: 'Button', value: '2' },
  ];

  const schema = yup.object().shape({
    name: yup
      .string()
      .max(255)
      .transform((curr, orig) => (orig.trim() === '' ? null : curr))
      .required('Tooltip name is required'),
    elementMetadata: yup.object().shape({
      elementType: yup.number().oneOf([1, 2], 'Type is required'),
    }),
    content: yup.string().required('Content is required'),
    id: yup.string().uuid().notRequired(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ElementFormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: tooltipData ? tooltipData.name : '',
      content: tooltipData ? tooltipData.content : '',
      elementMetadata: {
        elementType: tooltipData ? tooltipData.elementMetadata.type : 0,
        width: tooltipData ? tooltipData.elementMetadata.width : 0,
        height: tooltipData ? tooltipData.elementMetadata.height : 0,
        position: tooltipData
          ? tooltipData.elementMetadata.position
          : totalTooltip,
      },
      id: tooltipData ? tooltipData.id : undefined,
      frameId,
    },
  });

  const handleFieldChange = (e: any, fieldName: FieldNameType) => {
    const value = e.target ? e.target.value : e;
    setValue(fieldName, value);
  };

  const onSubmit = (data: ElementFormType) => {
    postElement(data);
  };

  useEffect(() => {
    if (
      postElementStatus.isSuccess &&
      !postElementStatus.isError &&
      postElementStatus.data
    ) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Tooltip ${tooltipData ? 'Edited' : 'Added'} Successfully`,
        },
      });
      onClose();
      onSave();
    }

    if (postElementStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  }, [postElementStatus]);

  const getErrorMessage = () => {
    const error = postElementStatus.error as any;
    return error.data && error.data.message;
  };

  return (
    <Modal
      id="ModalTooltip"
      type="medium"
      isOpened={isOpen}
      title={`${tooltipData ? 'Edit' : 'Add'} Tooltip`}
      closeModal={onClose}
    >
      <Fieldset>
        <FormContainer maxColumns={2}>
          <FormItem
            label="Type"
            message={
              errors.elementMetadata?.elementType &&
              errors.elementMetadata.elementType.message
            }
            status={errors.elementMetadata?.elementType ? 'error' : 'default'}
            size="md"
          >
            <NativeSelect
              id="FormTooltipType"
              {...register('elementMetadata.elementType', {})}
              onChange={(e) =>
                handleFieldChange(e, 'elementMetadata.elementType')
              }
              value={watch('elementMetadata.elementType')}
            >
              <option value="0" disabled hidden>
                Select Type
              </option>
              {noteTypes.map((noteType, index) => (
                <NativeSelect.Option
                  label={noteType.label}
                  value={noteType.value}
                  key={index}
                />
              ))}
            </NativeSelect>
          </FormItem>
          <FormItem
            label="Name"
            message={errors.name && errors.name.message}
            status={errors.name ? 'error' : 'default'}
            size="md"
          >
            <Input
              id="FormTooltipName"
              {...register('name', {})}
              onChange={(e) => handleFieldChange(e, 'name')}
              value={watch('name')}
              ariaLabel="name"
              placeholder="[Tooltip Name]"
              autoComplete="off"
            />
          </FormItem>
          <FormItem
            label="Content"
            message={errors.content && errors.content.message}
            status={errors.content ? 'error' : 'default'}
            className="note-form"
          >
            <CKEditorWrapper
              {...register('content', {})}
              id="TooltipContent"
              name="content"
              initData={tooltipData?.content}
              value={watch('content')}
              setValue={setValue}
              onChange={(e) => handleFieldChange(e, 'content')}
              toolbar="basic"
              config={getCkEditorConfig({
                tenantId: `${tenantId}`,
                type: 'elearnings',
                elearningId: `${elearningId}`,
              })}
            />
          </FormItem>
        </FormContainer>
      </Fieldset>
      {postElementStatus.isError && (
        <Alert type="danger" message={getErrorMessage()} />
      )}
      <div className="float-right">
        <Button
          onClick={onClose}
          htmlType="button"
          id="TooltipModalBtnCancel"
          isUppercase={false}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          isLoading={postElementStatus.isLoading}
          htmlType="button"
          ariaLabel="Submit Form"
          id="TooltipModalBtnSubmit"
          onClick={handleSubmit(onSubmit)}
          isUppercase={false}
          className="ml-2"
        >
          {`${tooltipData ? 'Save' : 'Add'} Tooltip`}
        </Button>
      </div>
    </Modal>
  );
};

export default TooltipModal;
